import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { useLocationStore } from '@/store/location';

export interface Permission {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
}

export interface UserPermission {
  permission_code: string;
  permission_name: string;
  category: string;
  granted: boolean;
  is_override: boolean;
}

export interface PermissionOverride {
  user_id: string;
  permission_id: string;
  granted: boolean;
}

export interface LocationPermissions {
  location_id: string;
  location_name: string;
  permissions: Record<string, boolean>;
}

export interface PermissionGroup {
  group_code: string;
  group_name: string;
  group_description: string | null;
  category: string;
  display_order: number;
  permissions: Permission[];
}

// Default permissions by role (client-side fallback)
const ROLE_PERMISSIONS: Record<string, string[]> = {
  superadmin: ['*'], // All permissions
  primary_admin: ['*'], // Primary admin has all permissions (like superadmin but org-scoped)
  owner: [
    'pos.access', 'pos.void_items', 'pos.apply_discounts', 'pos.no_sale', 'pos.reprint_receipt',
    'pos.void_entire_order', 'pos.apply_manual_discount', 'pos.override_price', 'pos.open_cash_drawer',
    'pos.accept_returns', 'pos.manage_tips', 'pos.training_mode',
    'orders.view', 'orders.create', 'orders.edit', 'orders.refund', 'orders.cancel', 'orders.reopen',
    'menu.view', 'menu.edit', 'menu.create', 'menu.delete', 'menu.86_items',
    'menu.view_cost', 'menu.update_prices', 'menu.manage_modifiers', 'menu.manage_categories',
    'menu.bulk_edit', 'menu.import_export',
    'customers.view', 'customers.create', 'customers.edit', 'customers.delete',
    'customers.view_history', 'customers.view_wallet', 'customers.manage_wallet', 'customers.export_data',
    'reports.view_sales', 'reports.view_labor', 'reports.view_inventory', 'reports.export',
    'reports.view_sales_summary', 'reports.view_sales_detailed', 'reports.view_product_performance',
    'reports.view_employee_performance', 'reports.view_customer_analytics', 'reports.view_financial',
    'reports.view_inventory_reports', 'reports.view_register_reports', 'reports.schedule_reports',
    'settings.view', 'settings.edit', 'settings.billing',
    'settings.manage_integrations', 'settings.manage_notifications', 'settings.manage_appearance',
    'team.view', 'team.create', 'team.edit', 'team.delete', 'team.impersonate',
    'team.view_schedules', 'team.manage_schedules', 'team.view_payroll', 'team.manage_payroll', 'team.manage_targets',
    'audit.view',
    'roles.view', 'roles.create', 'roles.edit', 'roles.delete', 'roles.assign',
    // Command Center & Inventory
    'backoffice.access', 'backoffice.products', 'backoffice.pricing', 'backoffice.locations', 'backoffice.suppliers',
    'inventory.view', 'inventory.adjust', 'inventory.count', 'inventory.create', 'inventory.settings',
    'inventory.view_stock', 'inventory.count_basic', 'inventory.approve_counts', 'inventory.view_cost',
    'inventory.request_transfers', 'inventory.adjust_without_approval', 'inventory.manage_suppliers',
    'inventory.approve_transfers', 'inventory.set_reorder_points',
    'inventory.purchase_orders.view', 'inventory.purchase_orders.create', 'inventory.purchase_orders.edit',
    'inventory.purchase_orders.receive', 'inventory.purchase_orders.cancel',
    'inventory.transfers.view', 'inventory.transfers.create', 'inventory.transfers.receive', 'inventory.transfers.cancel',
    'inventory.requests.view', 'inventory.requests.create', 'inventory.requests.approve',
    // Cash management
    'cash.manage',
  ],
  admin: [
    'pos.access', 'pos.void_items', 'pos.apply_discounts', 'pos.no_sale', 'pos.reprint_receipt',
    'pos.void_entire_order', 'pos.apply_manual_discount', 'pos.override_price', 'pos.open_cash_drawer',
    'pos.accept_returns', 'pos.manage_tips', 'pos.training_mode',
    'orders.view', 'orders.create', 'orders.edit', 'orders.refund', 'orders.cancel', 'orders.reopen',
    'menu.view', 'menu.edit', 'menu.create', 'menu.delete', 'menu.86_items',
    'menu.view_cost', 'menu.update_prices', 'menu.manage_modifiers', 'menu.manage_categories',
    'customers.view', 'customers.create', 'customers.edit', 'customers.delete',
    'customers.view_history', 'customers.view_wallet', 'customers.manage_wallet',
    'reports.view_sales', 'reports.view_labor', 'reports.view_inventory', 'reports.export',
    'reports.view_sales_summary', 'reports.view_sales_detailed', 'reports.view_product_performance',
    'reports.view_employee_performance', 'reports.view_customer_analytics', 'reports.view_financial',
    'settings.view', 'settings.edit',
    'team.view', 'team.create', 'team.edit', 'team.delete', 'team.impersonate',
    'team.view_schedules', 'team.manage_schedules', 'team.manage_targets',
    'audit.view',
    'roles.view', 'roles.edit', 'roles.assign',
    // Command Center & Inventory
    'backoffice.access', 'backoffice.products', 'backoffice.pricing', 'backoffice.locations', 'backoffice.suppliers',
    'inventory.view', 'inventory.adjust', 'inventory.count', 'inventory.create', 'inventory.settings',
    'inventory.view_stock', 'inventory.count_basic', 'inventory.approve_counts', 'inventory.view_cost',
    'inventory.request_transfers', 'inventory.adjust_without_approval', 'inventory.manage_suppliers',
    'inventory.approve_transfers', 'inventory.set_reorder_points',
    'inventory.purchase_orders.view', 'inventory.purchase_orders.create', 'inventory.purchase_orders.edit',
    'inventory.purchase_orders.receive', 'inventory.purchase_orders.cancel',
    'inventory.transfers.view', 'inventory.transfers.create', 'inventory.transfers.receive', 'inventory.transfers.cancel',
    'inventory.requests.view', 'inventory.requests.create', 'inventory.requests.approve',
    // Cash management
    'cash.manage',
  ],
  inventory_manager: [
    'menu.view', 'menu.edit', 'menu.view_cost',
    'orders.view',
    'reports.view_inventory', 'reports.view_inventory_reports',
    // Command Center (limited)
    'backoffice.access', 'backoffice.suppliers',
    // All inventory permissions (manager level)
    'inventory.view', 'inventory.adjust', 'inventory.count', 'inventory.create', 'inventory.settings',
    'inventory.view_stock', 'inventory.count_basic', 'inventory.approve_counts', 'inventory.view_cost',
    'inventory.request_transfers', 'inventory.adjust_without_approval', 'inventory.manage_suppliers',
    'inventory.approve_transfers', 'inventory.set_reorder_points',
    'inventory.purchase_orders.view', 'inventory.purchase_orders.create', 'inventory.purchase_orders.edit',
    'inventory.purchase_orders.receive', 'inventory.purchase_orders.cancel',
    'inventory.transfers.view', 'inventory.transfers.create', 'inventory.transfers.receive', 'inventory.transfers.cancel',
    'inventory.requests.view', 'inventory.requests.create', 'inventory.requests.approve',
  ],
  // New: Inventory Supervisor role
  inventory_supervisor: [
    'menu.view', 'menu.view_cost',
    'orders.view',
    'reports.view_inventory',
    // Inventory supervisor permissions
    'inventory.view', 'inventory.count',
    'inventory.view_stock', 'inventory.count_basic', 'inventory.approve_counts', 'inventory.view_cost',
    'inventory.request_transfers',
    'inventory.purchase_orders.view',
    'inventory.transfers.view', 'inventory.transfers.create', 'inventory.transfers.receive',
    'inventory.requests.view', 'inventory.requests.create', 'inventory.requests.approve',
  ],
  // New: Inventory Clerk role
  inventory_clerk: [
    'menu.view',
    'orders.view',
    // Basic inventory permissions
    'inventory.view', 'inventory.view_stock', 'inventory.count_basic',
    'inventory.purchase_orders.view',
    'inventory.transfers.view',
    'inventory.requests.view', 'inventory.requests.create',
  ],
  manager: [
    'pos.access', 'pos.void_items', 'pos.apply_discounts', 'pos.no_sale', 'pos.reprint_receipt',
    'pos.apply_manual_discount', 'pos.accept_returns', 'pos.training_mode',
    'orders.view', 'orders.create', 'orders.edit', 'orders.refund', 'orders.cancel',
    'menu.view', 'menu.edit', 'menu.create', 'menu.86_items',
    'customers.view', 'customers.create', 'customers.edit',
    'customers.view_history', 'customers.view_wallet',
    'reports.view_sales', 'reports.view_labor',
    'reports.view_sales_summary', 'reports.view_sales_detailed', 'reports.view_employee_performance',
    'team.view_schedules',
    // Inventory (limited)
    'inventory.view', 'inventory.adjust', 'inventory.count',
    'inventory.view_stock', 'inventory.count_basic', 'inventory.approve_counts',
    'inventory.transfers.view', 'inventory.transfers.receive',
    'inventory.requests.view', 'inventory.requests.create', 'inventory.requests.approve',
    // Cash management
    'cash.manage',
  ],
  // Lead Cashier role
  lead_cashier: [
    'pos.access', 'pos.void_items', 'pos.apply_discounts', 'pos.reprint_receipt',
    'pos.apply_manual_discount', 'pos.accept_returns',
    'orders.view', 'orders.create', 'orders.edit', 'orders.refund',
    'menu.view', 'menu.86_items',
    'customers.view', 'customers.create', 'customers.edit',
    'customers.view_history',
    'reports.view_sales_summary',
  ],
  inventory: [
    'menu.view',
    'orders.view',
    // Basic inventory access
    'inventory.view', 'inventory.count',
    'inventory.view_stock', 'inventory.count_basic',
    'inventory.purchase_orders.view',
    'inventory.transfers.view', 'inventory.transfers.receive',
    'inventory.requests.view', 'inventory.requests.create',
  ],
  cashier: [
    'pos.access', 'pos.reprint_receipt',
    'orders.view', 'orders.create',
    'menu.view',
    'customers.view', 'customers.create',
    'customers.view_history',
  ],
  employee: [
    'pos.access', 'pos.reprint_receipt',
    'orders.view', 'orders.create',
    'menu.view',
    'customers.view', 'customers.create',
  ],
  // Accounting role
  accountant: [
    'reports.view_sales', 'reports.view_sales_summary', 'reports.view_sales_detailed',
    'reports.view_financial', 'reports.view_register_reports', 'reports.export',
    'reports.view_inventory_reports',
    'menu.view', 'menu.view_cost',
    'inventory.view', 'inventory.view_stock', 'inventory.view_cost',
    'customers.view', 'customers.view_history',
  ],
};

export function usePermissions() {
  const { profile, user } = useAuth();
  const queryClient = useQueryClient();
  const { isImpersonating, impersonatedUser } = useImpersonation();
  const { currentLocation } = useLocationStore();

  // Compute effective role based on impersonation state
  const effectiveRole = isImpersonating && impersonatedUser
    ? impersonatedUser.role
    : profile?.role;

  // Fetch all permissions
  const { data: allPermissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('category')
        .order('name');

      if (error) throw error;
      return data as Permission[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour - permissions rarely change
  });

  // Fetch permission groups
  const { data: permissionGroups } = useQuery({
    queryKey: ['permission-groups'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_permission_groups');
      
      if (error) {
        console.warn('Permission groups not available:', error.message);
        return [];
      }
      return data as PermissionGroup[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Fetch user's effective permissions
  const { data: userPermissions, isLoading: userPermissionsLoading } = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .rpc('get_user_permissions', { p_user_id: user.id } as any);

      if (error) {
        console.error('Error fetching user permissions:', error);
        // Fallback to role-based permissions
        return [];
      }

      return data as UserPermission[];
    },
    enabled: !!user?.id,
  });

  // Fetch user permission overrides
  const { data: permissionOverrides } = useQuery({
    queryKey: ['user-permission-overrides', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_permissions')
        .select('*, permission:permissions(*)')
        .eq('user_id', user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Fetch location-specific permission overrides
  const { data: locationPermissions } = useQuery({
    queryKey: ['user-location-permissions', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .rpc('get_user_location_permissions', { p_user_id: user.id } as any);

      if (error) {
        console.error('Error fetching location permissions:', error);
        return [];
      }

      return data as LocationPermissions[];
    },
    enabled: !!user?.id,
  });

  // Check if user has a specific permission
  // When impersonating, uses the impersonated user's role permissions
  const hasPermission = (permissionCode: string): boolean => {
    // If not impersonating, need profile; if impersonating, need impersonatedUser
    if (!isImpersonating && !profile) return false;
    if (isImpersonating && !impersonatedUser) return false;

    // When impersonating, use impersonated user's role for permission checks
    // This ensures the admin sees exactly what the target user would see
    if (isImpersonating && impersonatedUser) {
      // Impersonated user's role determines permissions
      if (impersonatedUser.role === 'superadmin') return true;
      const rolePerms = ROLE_PERMISSIONS[impersonatedUser.role] || [];
      return rolePerms.includes(permissionCode) || rolePerms.includes('*');
    }

    // Not impersonating - use actual user's permissions
    // Superadmin has all permissions
    if (profile!.role === 'superadmin') return true;

    // Check user permissions from database
    if (userPermissions && userPermissions.length > 0) {
      const perm = userPermissions.find(p => p.permission_code === permissionCode);
      if (perm) return perm.granted;
    }

    // Fallback to role-based permissions
    const rolePerms = ROLE_PERMISSIONS[profile!.role] || [];
    return rolePerms.includes(permissionCode) || rolePerms.includes('*');
  };

  // Check if user has a specific permission at a specific location
  // Resolution order:
  // 1. Superadmin bypass
  // 2. Location-specific override
  // 3. Global permission check
  const hasPermissionAtLocation = (permissionCode: string, locationId?: string): boolean => {
    const targetLocationId = locationId || currentLocation?.id;

    // If not impersonating, need profile; if impersonating, need impersonatedUser
    if (!isImpersonating && !profile) return false;
    if (isImpersonating && !impersonatedUser) return false;

    // Superadmin bypass
    const role = isImpersonating ? impersonatedUser?.role : profile?.role;
    if (role === 'superadmin') return true;

    // Primary admin bypass (except superadmin-specific permissions)
    if (role === 'primary_admin' && permissionCode !== 'audit.view_superadmin') return true;

    // Check location-specific override
    if (targetLocationId && locationPermissions && locationPermissions.length > 0) {
      const locPerm = locationPermissions.find(lp => lp.location_id === targetLocationId);
      if (locPerm && locPerm.permissions && permissionCode in locPerm.permissions) {
        return locPerm.permissions[permissionCode];
      }
    }

    // Fall back to global permission check
    return hasPermission(permissionCode);
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    return permissionCodes.some(code => hasPermission(code));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    return permissionCodes.every(code => hasPermission(code));
  };

  // Grant or revoke a permission for a user
  const updateUserPermission = useMutation({
    mutationFn: async ({ 
      userId, 
      permissionCode, 
      granted 
    }: { 
      userId: string; 
      permissionCode: string; 
      granted: boolean | null; // null = remove override
    }) => {
      // First get the permission ID
      const { data: permission } = await supabase
        .from('permissions')
        .select('id')
        .eq('code', permissionCode)
        .single();

      if (!permission) throw new Error('Permission not found');

      if (granted === null) {
        // Remove the override
        const { error } = await (supabase
          .from('user_permissions') as any)
          .delete()
          .eq('user_id', userId)
          .eq('permission_id', (permission as any).id);

        if (error) throw error;
      } else {
        // Upsert the permission
        const { error } = await (supabase
          .from('user_permissions') as any)
          .upsert({
            user_id: userId,
            permission_id: (permission as any).id,
            granted,
            granted_by: user?.id,
          }, {
            onConflict: 'user_id,permission_id',
          });

        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-permissions', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['user-permission-overrides', variables.userId] });
    },
  });

  // Reset all permission overrides for a user
  const resetUserPermissions = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from('user_permissions')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-permission-overrides', userId] });
    },
  });

  // Update a location-specific permission
  const updateLocationPermission = useMutation({
    mutationFn: async ({
      userId,
      locationId,
      permissionCode,
      granted,
    }: {
      userId: string;
      locationId: string;
      permissionCode: string;
      granted: boolean | null; // null = remove override (revert to default)
    }) => {
      if (granted === null) {
        // Remove the override
        const { error } = await supabase
          .rpc('remove_location_permission', {
            p_user_id: userId,
            p_location_id: locationId,
            p_permission_code: permissionCode,
          } as any);

        if (error) throw error;
      } else {
        // Set the override
        const { error } = await supabase
          .rpc('set_location_permission', {
            p_user_id: userId,
            p_location_id: locationId,
            p_permission_code: permissionCode,
            p_granted: granted,
          } as any);

        if (error) throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-location-permissions', variables.userId] });
    },
  });

  // Reset all location-specific permissions for a user at a location
  const resetLocationPermissions = useMutation({
    mutationFn: async ({
      userId,
      locationId,
    }: {
      userId: string;
      locationId: string;
    }) => {
      const { error } = await supabase
        .rpc('reset_location_permissions', {
          p_user_id: userId,
          p_location_id: locationId,
        } as any);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-location-permissions', variables.userId] });
    },
  });

  // Get location permissions for another user (for editing)
  const getUserLocationPermissions = async (userId: string): Promise<LocationPermissions[]> => {
    const { data, error } = await supabase
      .rpc('get_user_location_permissions', { p_user_id: userId } as any);

    if (error) throw error;
    return data as LocationPermissions[];
  };

  // Get permissions grouped by category
  const getPermissionsByCategory = () => {
    if (!allPermissions) return {};

    return allPermissions.reduce((acc, perm) => {
      if (!acc[perm.category]) {
        acc[perm.category] = [];
      }
      acc[perm.category].push(perm);
      return acc;
    }, {} as Record<string, Permission[]>);
  };

  // Get permissions for another user (for editing)
  const getUserPermissions = async (userId: string): Promise<UserPermission[]> => {
    const { data, error } = await supabase
      .rpc('get_user_permissions', { p_user_id: userId } as any);

    if (error) throw error;
    return data as UserPermission[];
  };

  return {
    // Data
    permissions: allPermissions,
    userPermissions,
    permissionOverrides,
    locationPermissions,
    permissionsByCategory: getPermissionsByCategory(),
    permissionGroups: permissionGroups || [],

    // Loading states
    isLoading: permissionsLoading || userPermissionsLoading,

    // Permission checks
    hasPermission,
    hasPermissionAtLocation,
    hasAnyPermission,
    hasAllPermissions,

    // Mutations
    updateUserPermission,
    resetUserPermissions,
    updateLocationPermission,
    resetLocationPermissions,

    // Helpers
    getUserPermissions,
    getUserLocationPermissions,
    rolePermissions: ROLE_PERMISSIONS,

    // Impersonation-aware values
    effectiveRole,
    isImpersonating,

    // Current location context
    currentLocationId: currentLocation?.id,
  };
}

// Hook to check permission for conditional rendering
export function useHasPermission(permissionCode: string): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(permissionCode);
}

// Hook to check multiple permissions
export function useHasAnyPermission(permissionCodes: string[]): boolean {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissionCodes);
}

// Hook to check permission at current location
export function useHasPermissionAtLocation(permissionCode: string, locationId?: string): boolean {
  const { hasPermissionAtLocation } = usePermissions();
  return hasPermissionAtLocation(permissionCode, locationId);
}

